import React, { useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../i18n/translations';

const SPOTIFY_ARTIST_ID_REGEX = /^[A-Za-z0-9]{22}$/;

const getCanonicalSpotifyArtistUrl = (spotifyUrl) => {
    if (!spotifyUrl || typeof spotifyUrl !== 'string') return null;
    try {
        const parsed = new URL(spotifyUrl);
        if (!parsed.hostname.includes('spotify.com')) return null;
        const segments = parsed.pathname.split('/').filter(Boolean);
        const artistIndex = segments.findIndex((segment) => segment === 'artist');
        if (artistIndex < 0) return null;
        const artistId = segments[artistIndex + 1] || '';
        if (!SPOTIFY_ARTIST_ID_REGEX.test(artistId)) return null;
        return `https://open.spotify.com/artist/${artistId}`;
    } catch (error) {
        return null;
    }
};

// SVG paths da Simple Icons (simpleicons.org) — tutti white fill
const SpotifyIcon = () => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="white" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
    </svg>
);

const DeezerIcon = () => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="white" xmlns="http://www.w3.org/2000/svg">
        <path d="M18.944 17.33H24v-2.22h-5.056v2.22zm0-9.77v2.22H24V7.56h-5.056zm0 4.885H24v-2.22h-5.056v2.22zm-6.472 4.885h5.056v-2.22h-5.056v2.22zm0-9.77v2.22h5.056V7.56h-5.056zm0 4.885h5.056v-2.22h-5.056v2.22zM0 17.33h5.056v-2.22H0v2.22zm6.473 0h5.055v-2.22H6.473v2.22z"/>
    </svg>
);

const YouTubeMusicIcon = () => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="white" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 0C5.376 0 0 5.376 0 12s5.376 12 12 12 12-5.376 12-12S18.624 0 12 0zm0 19.104c-3.924 0-7.104-3.18-7.104-7.104S8.076 4.896 12 4.896s7.104 3.18 7.104 7.104-3.18 7.104-7.104 7.104zm0-13.332c-3.432 0-6.228 2.796-6.228 6.228S8.568 18.228 12 18.228s6.228-2.796 6.228-6.228S15.432 5.772 12 5.772zm-2.172 8.856V9.372l5.04 2.628-5.04 2.628z"/>
    </svg>
);

const AppleMusicIcon = () => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="white" xmlns="http://www.w3.org/2000/svg">
        <path d="M11.182.008C11.148-.03 9.923.023 8.857 1.18c-1.066 1.156-.902 2.482-.878 2.516.024.034 1.52.087 2.475-1.258.955-1.345.762-2.391.728-2.43zm3.314 11.733c-.048-.096-2.325-1.234-2.113-3.422.212-2.189 1.675-2.789 1.698-2.854.023-.065-.597-.79-1.254-1.157a3.692 3.692 0 0 0-1.563-.434c-.108-.003-.483-.095-1.254.116-.508.139-1.653.589-1.968.607-.316.018-1.256-.522-2.267-.665-.647-.125-1.333.131-1.824.328-.49.196-1.422.754-2.074 2.237-.652 1.482-.311 3.83-.067 4.56.244.729.625 1.924 1.273 2.796.576.984 1.34 1.667 1.659 1.899.319.232 1.219.386 1.843.067.502-.308 1.408-.485 1.766-.472.357.013 1.061.154 1.782.539.571.197 1.111.115 1.652-.105.541-.221 1.324-1.059 2.238-2.758.347-.925.596-1.844.67-2.301z"/>
    </svg>
);

const YouTubeIcon = () => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="white" xmlns="http://www.w3.org/2000/svg">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
    </svg>
);

const TidalIcon = () => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="white" xmlns="http://www.w3.org/2000/svg">
        <path d="M12.012 3.992L8.008 7.996 12.012 12 16.016 7.996zM8.008 7.996L4.004 12.001l4.004 4.004 4.004-4.004zM16.016 7.996l-4.004 4.004 4.004 4.004 4.004-4.004zM8.008 16.005l4.004 4.004 4.004-4.004-4.004-4.004z"/>
    </svg>
);

const PLATFORMS = [
    {
        id: 'spotify',
        name: 'Spotify',
        Icon: SpotifyIcon,
        getUrl: (name, ids) => {
            if (ids?.spotifyId && SPOTIFY_ARTIST_ID_REGEX.test(ids.spotifyId)) {
                return `https://open.spotify.com/artist/${ids.spotifyId}`;
            }
            const canonicalArtistUrl = getCanonicalSpotifyArtistUrl(ids?.spotifyUrl);
            if (canonicalArtistUrl) return canonicalArtistUrl;
            return `https://open.spotify.com/search/${encodeURIComponent(name)}`;
        },
        getAlbumUrl: (albumName, artistName) =>
            `https://open.spotify.com/search/${encodeURIComponent(`${albumName} ${artistName}`)}`,
    },
    {
        id: 'deezer',
        name: 'Deezer',
        Icon: DeezerIcon,
        getUrl: (name, ids) =>
            ids?.deezerId
                ? `https://www.deezer.com/artist/${ids.deezerId}`
                : `https://www.deezer.com/search/${encodeURIComponent(name)}`,
        getAlbumUrl: (albumName, artistName, ids) =>
            ids?.deezerAlbumId
                ? `https://www.deezer.com/album/${ids.deezerAlbumId}`
                : `https://www.deezer.com/search/${encodeURIComponent(`${albumName} ${artistName}`)}`,
    },
    {
        id: 'youtube_music',
        name: 'YouTube Music',
        Icon: YouTubeMusicIcon,
        getUrl: (name) =>
            `https://music.youtube.com/search?q=${encodeURIComponent(name)}`,
        getAlbumUrl: (albumName, artistName) =>
            `https://music.youtube.com/search?q=${encodeURIComponent(`${albumName} ${artistName}`)}`,
    },
    {
        id: 'apple_music',
        name: 'Apple Music',
        Icon: AppleMusicIcon,
        getUrl: (name) =>
            `https://music.apple.com/search?term=${encodeURIComponent(name)}`,
        getAlbumUrl: (albumName, artistName) =>
            `https://music.apple.com/search?term=${encodeURIComponent(`${albumName} ${artistName}`)}`,
    },
    {
        id: 'youtube',
        name: 'YouTube',
        Icon: YouTubeIcon,
        getUrl: (name) =>
            `https://www.youtube.com/results?search_query=${encodeURIComponent(name + ' music')}`,
        getAlbumUrl: (albumName, artistName) =>
            `https://www.youtube.com/results?search_query=${encodeURIComponent(`${albumName} ${artistName} full album`)}`,
    },
    {
        id: 'tidal',
        name: 'Tidal',
        Icon: TidalIcon,
        getUrl: (name) =>
            `https://tidal.com/browse/search?q=${encodeURIComponent(name)}`,
        getAlbumUrl: (albumName, artistName) =>
            `https://tidal.com/browse/search?q=${encodeURIComponent(`${albumName} ${artistName}`)}`,
    },
];

const ListenModal = ({
    isOpen,
    onClose,
    artistName,
    albumName = null,
    ids = {},
    mode = 'listen', // 'listen' | 'share'
}) => {
    const { language } = useLanguage();
    const { t } = useTranslation(language);

    useEffect(() => {
        if (!isOpen) return;
        const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const title = albumName ? `"${albumName}"` : artistName;
    const listenOnLabel = mode === 'share'
        ? t('showResult.shareLink')
        : (language === 'it' ? 'ascolta su' : 'listen on');

    const handlePlatformClick = async (e, url, platformName) => {
        if (mode !== 'share') return; // apertura normale (link target=_blank)
        e.preventDefault();
        const shareTitle = language === 'it'
            ? `Scopri ${artistName} con Foundly`
            : `Discover ${artistName} with Foundly`;
        const shareText = language === 'it'
            ? `Ho scoperto ${artistName} con foundly.it\n\nAscolta su ${platformName}.`
            : `I discovered ${artistName} with foundly.it\n\nListen on ${platformName}.`;
        if (navigator.share) {
            try {
                await navigator.share({ title: shareTitle, text: shareText, url });
            } catch (err) {
                if (err.name !== 'AbortError') console.error('Share error:', err);
            }
        } else {
            navigator.clipboard?.writeText(url).catch(() => {});
        }
        onClose();
    };

    return (
        <>
            {/* Overlay */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed', inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    zIndex: 9999,
                }}
            />

            {/* Modal centrato — leggermente più in alto */}
            <div style={{
                position: 'fixed',
                top: '38%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '80vw',
                maxWidth: '400px',
                backgroundColor: '#1a1a1a',
                borderRadius: '20px',
                zIndex: 10000,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                animation: 'fadeScaleIn 0.2s ease',
            }}>
                {/* X button */}
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '14px',
                        right: '14px',
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.2)',
                        backgroundColor: 'rgba(255,255,255,0.06)',
                        color: 'white',
                        fontSize: '16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1,
                        zIndex: 1,
                    }}
                    aria-label={language === 'it' ? 'Chiudi' : 'Close'}
                >
                    ✕
                </button>

                {/* Header */}
                <div style={{ padding: '22px 54px 10px 20px', flexShrink: 0 }}>
                    <h3 style={{
                        fontSize: '20px',
                        fontWeight: '700',
                        color: 'white',
                        margin: '0 0 3px',
                        lineHeight: 1.25,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}>
                        {title}
                    </h3>
                    <p style={{
                        fontSize: '12px',
                        color: 'rgba(255,255,255,0.35)',
                        margin: 0,
                        fontWeight: '400',
                    }}>
                        {listenOnLabel}
                    </p>
                </div>

                {/* Platform list */}
                <div style={{ padding: '4px 8px 40px' }}>
                    {PLATFORMS.map((platform) => {
                        const url = albumName
                            ? platform.getAlbumUrl(albumName, artistName, ids)
                            : platform.getUrl(artistName, ids);

                        return (
                            <a
                                key={platform.id}
                                href={url}
                                target={mode === 'share' ? undefined : '_blank'}
                                rel="noopener noreferrer"
                                onClick={(e) => handlePlatformClick(e, url, platform.name)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    padding: '9px 12px',
                                    borderRadius: '10px',
                                    textDecoration: 'none',
                                    color: 'white',
                                    transition: 'background 0.15s',
                                    cursor: 'pointer',
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            >
                                <span style={{
                                    width: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}>
                                    <platform.Icon />
                                </span>
                                <span style={{
                                    fontSize: '15px',
                                    fontWeight: '500',
                                    color: 'white',
                                }}>
                                    {platform.name}
                                </span>
                                <i className="bi bi-arrow-up-right" style={{
                                    marginLeft: 'auto',
                                    fontSize: '13px',
                                    color: 'rgba(255,255,255,0.25)',
                                }} />
                            </a>
                        );
                    })}
                </div>
            </div>

            <style>{`
                @keyframes fadeScaleIn {
                    from { opacity: 0; transform: translate(-50%, -50%) scale(0.94); }
                    to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
                }
            `}</style>
        </>
    );
};

export default ListenModal;
